/**
 * The far tier, instanced (67.17): every distant person of one palette in one pose is a single instanced draw.
 * A person at the silhouette tier (6–24 px) hides its own model and adds its placement here each frame; the crowd
 * draws one InstancedMesh per (palette, pose) — at most teams × six poses, in practice two teams in two or three
 * poses — however many people that is. The batches' geometry is the person's own baked silhouette pose
 * (personModel.ts), taken once per palette, so a far figure looks exactly as before. Presentation only.
 */
import * as THREE from 'three';
import type { PersonModel, SilhouettePose } from './personModel';

interface Batch { mesh: THREE.InstancedMesh; count: number }

const _rootInv = new THREE.Matrix4(), _rel = new THREE.Matrix4(), _unmirror = new THREE.Matrix4(), _placed = new THREE.Matrix4();
/** 67.21: the mirror across a person's centreline (their local x). */
const MIRROR_X = new THREE.Matrix4().makeScale(-1, 1, 1);

/** Mirror a geometry across x in place, keeping its faces outward: the positions and normals flipped, each triangle's winding reversed. */
export function mirrorGeometryX(geo: THREE.BufferGeometry): void {
  geo.applyMatrix4(MIRROR_X);
  const idx = geo.index;
  if (idx) {
    for (let i = 0; i + 2 < idx.count; i += 3) { const b = idx.getX(i + 1); idx.setX(i + 1, idx.getX(i + 2)); idx.setX(i + 2, b); }
    idx.needsUpdate = true;
    return;
  }
  for (const attr of Object.values(geo.attributes) as THREE.BufferAttribute[]) {
    const n = attr.itemSize;
    for (let v = 0; v + 2 < attr.count; v += 3) for (let c = 0; c < n; c++) {
      const a = attr.array[(v + 1) * n + c]; attr.array[(v + 1) * n + c] = attr.array[(v + 2) * n + c]; attr.array[(v + 2) * n + c] = a;
    }
    attr.needsUpdate = true;
  }
}

export class SilhouetteCrowd {
  private readonly batches = new Map<string, Batch>();
  /** per palette: each pose's geometry in the person's root space (person units), and its material */
  private readonly templates = new Map<string, Map<SilhouettePose, { geo: THREE.BufferGeometry; mat: THREE.Material }>>();

  constructor(private readonly scene: THREE.Scene, private readonly initial = 32) {}

  /** Start a frame: every batch empties. */
  begin(): void { for (const b of this.batches.values()) b.count = 0; }

  /** Draw `model` (posed and placed this frame) at the far tier, in its current silhouette pose. */
  add(model: PersonModel, paletteKey: string): void {
    const pose = model.silhouettePose;
    // 67.21: a right-handed person is the model mirrored; an instance can't flip its own winding, so the right-handed
    // batches draw a mirrored template under the instance's matrix with the mirror taken out
    const mirrored = model.root.scale.x < 0;
    const key = `${paletteKey}|${model.kitKey}|${pose}|${mirrored ? 'R' : 'L'}`;   // 65.17 (b): an armored figure has its own template
    let b = this.batches.get(key);
    if (!b) {
      const t = this.template(model, `${paletteKey}|${model.kitKey}`, mirrored).get(pose)!;
      b = this.makeBatch(t.geo, t.mat, this.initial);
      this.batches.set(key, b);
    } else if (b.count >= b.mesh.instanceMatrix.count) {
      b = this.grow(key, b);
    }
    model.root.updateWorldMatrix(true, false);
    // 66.7 (a): the far tier's own lean (a person leaning out of cover) — the templates are in its space, so it rides the instance
    model.silhouette.updateMatrix();
    _placed.multiplyMatrices(model.root.matrixWorld, model.silhouette.matrix);
    b.mesh.setMatrixAt(b.count++, mirrored ? _unmirror.multiplyMatrices(_placed, MIRROR_X) : _placed);
  }

  /** End a frame: each batch draws what was added (an empty batch draws nothing). */
  end(): void {
    for (const b of this.batches.values()) {
      b.mesh.count = b.count;
      b.mesh.visible = b.count > 0;
      if (b.count > 0) b.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** The batches drawing this frame (for tests and the F3 overlay's reading). */
  drawing(): Array<{ key: string; count: number }> {
    return [...this.batches.entries()].filter(([, b]) => b.count > 0).map(([key, b]) => ({ key, count: b.count }));
  }

  dispose(): void {
    for (const b of this.batches.values()) { this.scene.remove(b.mesh); b.mesh.dispose(); }
    for (const t of this.templates.values()) for (const { geo, mat } of t.values()) { geo.dispose(); mat.dispose(); }
    this.batches.clear(); this.templates.clear();
  }

  /** The palette's silhouette poses, from the first model seen in it: each baked pose mesh carried into the far tier's own space (its lean left out). */
  private template(model: PersonModel, paletteKey: string, mirrored = false): Map<SilhouettePose, { geo: THREE.BufferGeometry; mat: THREE.Material }> {
    const tkey = `${paletteKey}|${mirrored ? 'R' : 'L'}`;
    let t = this.templates.get(tkey);
    if (t) return t;
    t = new Map();
    model.root.updateMatrixWorld(true);
    _rootInv.copy(model.silhouette.matrixWorld).invert();
    for (const [pose, q] of Object.entries(model.silhouettePoses) as Array<[SilhouettePose, THREE.Group]>) {
      const geos: THREE.BufferGeometry[] = [];
      let mat: THREE.Material | null = null;
      q.traverse(o => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        geos.push(m.geometry.clone().applyMatrix4(_rel.multiplyMatrices(_rootInv, m.matrixWorld)));
        mat ??= (Array.isArray(m.material) ? m.material[0] : m.material).clone();
      });
      // a baked pose is one mesh; were it ever more, the first stands in (the bake test holds it to one)
      t.set(pose, { geo: geos[0] ?? new THREE.BufferGeometry(), mat: mat ?? new THREE.MeshLambertMaterial() });
      for (const g of geos.slice(1)) g.dispose();
    }
    if (mirrored) for (const v of t.values()) mirrorGeometryX(v.geo);
    this.templates.set(tkey, t);
    return t;
  }

  private makeBatch(geo: THREE.BufferGeometry, mat: THREE.Material, capacity: number): Batch {
    const mesh = new THREE.InstancedMesh(geo, mat, capacity);
    mesh.name = 'crowd';
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;   // the instances spread over a planet; one draw either way
    mesh.count = 0;
    mesh.visible = false;
    this.scene.add(mesh);
    return { mesh, count: 0 };
  }

  private grow(key: string, b: Batch): Batch {
    const next = this.makeBatch(b.mesh.geometry, b.mesh.material as THREE.Material, b.mesh.instanceMatrix.count * 2);
    const m = new THREE.Matrix4();
    for (let i = 0; i < b.count; i++) { b.mesh.getMatrixAt(i, m); next.mesh.setMatrixAt(i, m); }
    next.count = b.count;
    this.scene.remove(b.mesh);
    b.mesh.dispose();   // the instance buffer only: InstancedMesh.dispose leaves the shared geometry and material
    this.batches.set(key, next);
    return next;
  }
}
